import { jsPDF } from 'jspdf';
import { registerRoboto } from "./fonts/registerRoboto"; // font loader
import { hexToRgb, groupByEColumn, getOrdinalSuffix } from './utils/helpers';
import { getPassportImagePaths, loadImageAsDataURL, loadImageFromPublic } from './utils/imageLoader';
//import { normalizeFolderKey } from './utils/normalizeFolderKey';
import { loadAllFolders } from './utils/imageImports';




const folderMap = loadAllFolders();

// Global tracker (declare once near top)
const folderImageTracker = {};
// Track per-student rotation in semi mode
const studentRotationTracker = {};



// ✅ Custom page sizes mapping
const customPageSizes = {
  a1: [594, 841],  // mm
  a2: [420, 594],
  a3: [297, 420],
  a4: [210, 297],

  // ✅ ADD THIS (Your new custom size)
  acustom: [305, 460], // width=300mm height=450mm (Portrait)
};



// ✅ Main exported function
export async function generatePDF({
  
  sheet1Data,
  sheet2Data,
  pageSize,
  orientation,
  labels,
  classLabel,
  fontStyles,
  labelColors,
  valueColors,
  lineColors,
  setGridCounts,
  setTotalGrids,
  mode = "all", // 👈 new parameter, "all" or "semi"
  selectedBorderImages,
}) {
  function importAllImages(r) {
    return r.keys().map(r);
  }
  function normalizeFolderKey(value) {
  if (!value) return '';
  return String(value).trim().toLowerCase().replace(/\s+/g, '');
}

  

 // Border images can be explicitly selected in the frontend. When the
// parameter is supplied (including an empty array), use that exact selection.
// Older callers that omit it retain the previous public-folder auto-loading.
const borderImages = [];
let borderImageNames = [];

if (Array.isArray(selectedBorderImages)) {
  borderImages.push(...selectedBorderImages.filter(
    image => typeof image === 'string' && image.startsWith('data:image/')
  ));
} else {
  try {
    const response = await fetch(`${process.env.PUBLIC_URL}/borderImages.txt`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const text = await response.text();
    borderImageNames = text.split(/\\r?\\n/).map(line => line.trim()).filter(Boolean);
  } catch (err) {
    console.error("Error loading borderImages.txt:", err);
    borderImageNames = ['b1.jpeg', 'b2.jpeg', 'b3.jpeg', 'b4.jpeg', 'b5.jpeg'];
  }
}

  // ✅ Load dynamic images (new addition)
  const dynamicPassportImages = getPassportImagePaths()?.map((img) => ({
    name: img.name?.toLowerCase() || img.src.split('/').pop().split('.')[0].toLowerCase(),
    src: img.src,
  })) || [];
 
    const folder1Images = importAllImages(require.context('./images/1', false, /\.(png|jpe?g)$/));
    const folder2Images = importAllImages(require.context('./images/2', false, /\.(png|jpe?g)$/));
    const folder3Images = importAllImages(require.context('./images/3', false, /\.(png|jpe?g)$/));
    const folder4Images = importAllImages(require.context('./images/4', false, /\.(png|jpe?g)$/));
    
	const folder5Images = importAllImages(require.context('./images/5', false, /\.(png|jpe?g)$/));
    const folder6Images = importAllImages(require.context('./images/6', false, /\.(png|jpe?g)$/));
    
	const folder7Images = importAllImages(require.context('./images/7', false, /\.(png|jpe?g)$/));
    const folder8Images = importAllImages(require.context('./images/8', false, /\.(png|jpe?g)$/));
    
	const folder9Images = importAllImages(require.context('./images/9', false, /\.(png|jpe?g)$/));
    const folder10Images = importAllImages(require.context('./images/10', false, /\.(png|jpe?g)$/));
    
	const lkgImages = importAllImages(require.context('./images/lkg', false, /\.(png|jpe?g)$/));
    const ukgImages = importAllImages(require.context('./images/ukg', false, /\.(png|jpe?g)$/));
    const interImages = importAllImages(require.context('./images/inter', false, /\.(png|jpe?g)$/));
	const studentsImages = importAllImages(require.context('./images/students', false, /\.(png|jpe?g)$/));
    
  
  const basePassportImagePaths = [
  ...folder1Images.map((img) => ({ name: img.split('/').pop().split('.')[0].toLowerCase(), src: img })),
  ...folder2Images.map((img) => ({ name: img.split('/').pop().split('.')[0].toLowerCase(), src: img })),
  ...folder3Images.map((img) => ({ name: img.split('/').pop().split('.')[0].toLowerCase(), src: img })),
  ...folder4Images.map((img) => ({ name: img.split('/').pop().split('.')[0].toLowerCase(), src: img })),
  ...folder5Images.map((img) => ({ name: img.split('/').pop().split('.')[0].toLowerCase(), src: img })),
  ...folder6Images.map((img) => ({ name: img.split('/').pop().split('.')[0].toLowerCase(), src: img })),
  ...folder7Images.map((img) => ({ name: img.split('/').pop().split('.')[0].toLowerCase(), src: img })),
  ...folder8Images.map((img) => ({ name: img.split('/').pop().split('.')[0].toLowerCase(), src: img })),
  ...folder9Images.map((img) => ({ name: img.split('/').pop().split('.')[0].toLowerCase(), src: img })),
  ...folder10Images.map((img) => ({ name: img.split('/').pop().split('.')[0].toLowerCase(), src: img })),
  ...lkgImages.map((img) => ({ name: img.split('/').pop().split('.')[0].toLowerCase(), src: img })),
  ...ukgImages.map((img) => ({ name: img.split('/').pop().split('.')[0].toLowerCase(), src: img })),
  ...interImages.map((img) => ({ name: img.split('/').pop().split('.')[0].toLowerCase(), src: img })),
];

  // ✅ Load student images separately for matching logic
  const studentImagePaths = studentsImages.map((img) => ({
    name: img.split('/').pop().split('.')[0].toLowerCase(),
    src: img,
  }));




  // ✅ Match student images to Sheet1 Column[2] (unique usage only)
  const usedStudentImages = new Set();
  const studentMatchedImages = [];
  const unmatchedStudents = [];

  sheet1Data.forEach((row) => {
    const col2Value = row[2]?.trim().toLowerCase();
    const match = studentImagePaths.find(
      (img) => img.name === col2Value && !usedStudentImages.has(img.name)
    );
    if (match) {
      studentMatchedImages.push(match);
      usedStudentImages.add(match.name);
    } else {
      unmatchedStudents.push(col2Value);
    }
  });

  // ✅ Final merged passportImagePaths
  const passportImagePaths = [
    ...studentMatchedImages, // first priority
    ...dynamicPassportImages.filter((img) => !usedStudentImages.has(img.name)), // second
    ...basePassportImagePaths.filter((img) => !usedStudentImages.has(img.name)), // fallback
  ];

  // ✅ Preload all images (borders + passports)
  const borderPromises = Array.isArray(selectedBorderImages)
    ? []
    : borderImageNames.map((name) =>
        loadImageFromPublic(`${process.env.PUBLIC_URL}/images/${name}`)
      );
  const passportPromises = passportImagePaths.map((img) => loadImageAsDataURL(img.src));

  const allImages = await Promise.all([...borderPromises, ...passportPromises]);
  // Ignore missing optional public borders; selected frontend borders are
  // already decoded as data URLs and are used directly.
  if (!Array.isArray(selectedBorderImages)) {
    borderImages.push(
      ...allImages
        .slice(0, borderImageNames.length)
        .filter((image) => typeof image === 'string' && image.startsWith('data:image/'))
    );
  }
  const loadedPassports = allImages.slice(borderPromises.length);

  // ✅ Map passport names to image data
  const passportImagesMap = {};
  passportImagePaths.forEach((img, i) => {
    passportImagesMap[img.name.toLowerCase()] = loadedPassports[i];
  });
  
  
  

// Build general fallback from passportImagePaths -> loadedPassports (already-loaded DataURLs)
// Use column index 2 for matching (Sheet1 Column[2])
const matchedKeys = sheet1Data.map((r) => r[2]?.trim().toLowerCase()).filter(Boolean);

const generalFallbackPool = passportImagePaths.reduce((acc, p, idx) => {
  const nm = p.name?.toLowerCase();
  if (!matchedKeys.includes(nm)) acc.push(loadedPassports[idx]);
  return acc;
}, []);
// if no general fallback items, keep loadedPassports as fallback pool (but we won't use it for strict folder matches)
const generalPool = generalFallbackPool.length ? generalFallbackPool : loadedPassports.slice();

// Identify folder keys actually referenced in sheets
const referencedFolderKeys = new Set();
[...sheet1Data, ...sheet2Data].forEach((r) => {
  const k = normalizeFolderKey(r?.[5]);
  if (k && folderMap[k] && folderMap[k].length) referencedFolderKeys.add(k);
});

// --------------------
// Preload ALL folder images as DataURLs (safer than only referenced ones)
// --------------------
// -----------------------------
// Preload ALL folder images as dataURLs (inside generatePDF, early)
// -----------------------------
const folderFallbacks = {};
const allFolderKeys = Object.keys(folderMap || {});

for (const key of allFolderKeys) {
  const srcList = folderMap[key] || [];
  try {
    // require.context returns module paths and your importAllImages produced them;
    // loadImageAsDataURL accepts that src and returns a dataURL
    const loaded = await Promise.all(srcList.map((src) => loadImageAsDataURL(src)));
    folderFallbacks[key] = loaded.filter(Boolean); // dataURLs
  } catch (err) {
    
    folderFallbacks[key] = [];
  }
}


// counters for strict rotation
const folderCounters = {};
let generalCounter = 0;





  // ✅ Start PDF generation
  // ✅ Determine custom size if selected
let finalPageSize = pageSize;

if (pageSize === "acustom") {
  finalPageSize = orientation === "portrait"
    ? [305, 460]   // 30cm x 45cm
    : [460, 305];  // 45cm x 30cm (landscape)
}

// ✅ Create PDF with dynamic size
const doc = new jsPDF({
  orientation,
  unit: "mm",
  format: finalPageSize,
  encryption: {
        //userPassword: "open123",            // password to open (optional but more reliable if set)
        //ownerPassword: "ownerSecret!",      // controls permissions
        // Only list what you want to ALLOW. Omit "copy".
        userPermissions: ["print"]          // allow printing, but NOT copying
      }
});
 

  registerRoboto(doc);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const gap = 0;
  const gridWidth = 89;
  const gridHeight = 57;
  let finalCols = Math.floor((pageWidth + gap) / (gridWidth + gap));
  let finalRows = Math.floor((pageHeight + gap) / (gridHeight + gap));
  let maxGridsPerPage = finalCols * finalRows;

  // ✅ Fix for A4 portrait layout
  if (pageSize === 'a4' && orientation === 'portrait') {
    finalCols = 2;
    finalRows = 4;
    maxGridsPerPage = 8;
  }
  
 


  const usedWidth = finalCols * gridWidth + (finalCols - 1) * gap;
  const usedHeight = finalRows * gridHeight + (finalRows - 1) * gap;
 let marginX = (pageWidth - usedWidth) / 2;
let marginY = (pageHeight - usedHeight) / 2;


  const sheet1Groups = groupByEColumn(sheet1Data);
  const sheet2Groups = groupByEColumn(sheet2Data);
  const counts = {};
  let total = 0;
  
  
  
   
 // ✅ Custom mode — Landscape (30×45 cm → 450mm × 300mm)
// ✅ Exact 5×5 grids without shrinking grid size (only reduce margins)
if (pageSize === "acustom" && orientation === "landscape") {
  
  finalCols = 5;      // 5 columns
  finalRows = 5;      // 5 rows
  maxGridsPerPage = 25;

  // ✅ Recalculate margins for perfect fit
  const totalGridWidth = finalCols * gridWidth + (finalCols - 1) * gap;   // 448 mm
  const totalGridHeight = finalRows * gridHeight + (finalRows - 1) * gap; // 288 mm

  marginX = (pageWidth - totalGridWidth) / 2;   // ~1 mm side margins
  marginY = (pageHeight - totalGridHeight) / 2; // ~6 mm top-bottom margins
}

  
  
  
  
  
  
  
  //to write empty space after each student labels
  
// ✅ Helper: Fill remaining blank grids (enlarged visuals, 1.5 mm lower)
const fillRemainingGrids = (
  startGridCount,
  doc,
  maxGridsPerPage,
  finalCols,
  finalRows,
  gridWidth,
  gridHeight,
  gap,
  marginX,
  marginY
) => {
  const remaining = maxGridsPerPage - (startGridCount % maxGridsPerPage);
  if (remaining === 0 || remaining === maxGridsPerPage) return;

  // ⚙️ Adjustment controls
  const extraBorderWidth = 0;   // +2 mm width
  const extraBorderHeight = 0;  // +3 mm height
  const extraPhotoWidth = 2.3;    // +5 mm width
  const extraPhotoHeight = 3.5;   // +6 mm height
  const photoOffsetX = 0.4;      // 1 mm left
  const photoOffsetY = 4;      // 4 mm down
  const downShift = 0;        // 👈 shift border & photo 1.5 mm down

  for (let r = 0; r < remaining; r++) {
    const gridIndex = startGridCount + r;
    const col = gridIndex % finalCols;
    const row = Math.floor((gridIndex / finalCols) % finalRows);
    const x = marginX + col * (gridWidth + gap);
    const y = marginY + row * (gridHeight + gap);

    // ✅ Border (drawn slightly larger, 1.5 mm lower)
    const borderImg = borderImages[r % borderImages.length];
    if (borderImg) {
      try {
        const imgData = borderImg && borderImg.default ? borderImg.default : borderImg;
        const fmt = (typeof imgData === "string" && imgData.startsWith("data:image/png")) ? "PNG" : "JPEG";

        const borderX = x - extraBorderWidth / 2;
        const borderY = y - extraBorderHeight / 2 + downShift; // ⬇ shifted
        const borderW = gridWidth + extraBorderWidth;
        const borderH = gridHeight + extraBorderHeight;

        doc.addImage(imgData, fmt, borderX, borderY, borderW, borderH);
		// Dummy Label Kiss Cut
doc.setDrawColor(255, 0, 255);
doc.setLineWidth(0.05);

const cutInset = 0.15;

doc.rect(
  x + cutInset,
  y + cutInset,
  gridWidth - (cutInset * 2),
  gridHeight - (cutInset * 2)
);
      } catch {
        doc.setDrawColor(220, 220, 220);
        doc.rect(x, y + downShift, gridWidth, gridHeight); // ⬇ fallback shifted
      }
    }

    // ✅ Enlarged photo (shifted down 1.5 mm)
    const basePhotoX = x + 6;
    const basePhotoY = y + 16;
    const basePhotoWidth = 18;
    const basePhotoHeight = 22;
    const radius = 2.5;

    const photoWidth = basePhotoWidth + extraPhotoWidth;
    const photoHeight = basePhotoHeight + extraPhotoHeight;
    const photoX = basePhotoX + photoOffsetX - extraPhotoWidth / 2;
    const photoY = basePhotoY + photoOffsetY - extraPhotoHeight / 2 + downShift; // ⬇ shifted

    let fallbackPhoto = null;
    if (Array.isArray(generalPool) && generalPool.length > 0) {
      fallbackPhoto = generalPool[r % generalPool.length];
    }

    if (fallbackPhoto) {
      try {
        const imgData = fallbackPhoto && fallbackPhoto.default ? fallbackPhoto.default : fallbackPhoto;
        const fmt = (typeof imgData === "string" && imgData.startsWith("data:image/png")) ? "PNG" : "JPEG";
        doc.addImage(imgData, fmt, photoX, photoY, photoWidth, photoHeight);
        doc.setDrawColor(128, 0, 128);
        doc.setLineWidth(0.35);
        doc.roundedRect(photoX, photoY, photoWidth, photoHeight, radius, radius, "S");
      } catch {
        doc.setDrawColor(180, 180, 180);
        doc.roundedRect(photoX, photoY, photoWidth, photoHeight, radius, radius, "S");
      }
    } else {
      doc.setDrawColor(180, 180, 180);
      doc.roundedRect(photoX, photoY, photoWidth, photoHeight, radius, radius, "S");
    }

    // ✅ Labels (unchanged)
    const textX = x + 27;
    const textY = y + 20;
    const gridFont = fontStyles[0];
    const staticLabels = [
      labels.name,
      labels.study,
      labels.admNo,
      "Subject",
      //labels.school
    ].filter(Boolean);

    staticLabels.forEach((lbl, i) => {
      const labelColor = labelColors[i % labelColors.length];
      const lineColor = lineColors[i % lineColors.length];
      const rowOffset = i * 8.2;

      doc.setFont(gridFont.font, gridFont.fontWeight || "normal");
      doc.setFontSize(gridFont.size);
      doc.setTextColor(...hexToRgb(labelColor));
      doc.text(`${lbl}:`, textX, textY + rowOffset);

      doc.setDrawColor(...hexToRgb(lineColor));
      doc.setLineWidth(0.25);
      doc.line(
        textX,
        textY + 1.5 + rowOffset,
        textX + gridWidth - 30.4 - 1, //to reduce line size 2px
        textY + 1.5 + rowOffset
      );
    });
  }
};


  // ✅ Helper to write text rows
  const writeRow = (label, value, lineIndex, font, x, y, isFirstRow = false, offset = 0) => {
    const labelColor = labelColors[lineIndex % labelColors.length];
    const valueColor = valueColors[lineIndex % valueColors.length];
    const lineColor = lineColors[lineIndex % lineColors.length];
    const labelMaxWidth = 16;
    const spacing = -3;

    const appliedOffset = isFirstRow ? 0 : offset;
    doc.setFont(font.font, font.fontWeight || 'normal');
    doc.setFontSize(font.size);
    doc.setTextColor(...hexToRgb(labelColor));
    doc.text(label, x, y + appliedOffset);

    doc.setTextColor(...hexToRgb(valueColor));
    const valueX = x + labelMaxWidth + spacing;
    doc.text(value, valueX, y + appliedOffset);

    doc.setDrawColor(...hexToRgb(lineColor));
    doc.setLineWidth(0.25);
    doc.line(x, y + 1.5 + appliedOffset, x + gridWidth - 30.4, y + 1.5 + appliedOffset);
  };

  let gridCount = 0;
  const orderedEValues = [];
  sheet1Data.forEach((row) => {
    const eVal = row[4]?.toString().trim();
    if (eVal && !orderedEValues.includes(eVal)) orderedEValues.push(eVal);
  });

  // ✅ Iterate group-wise
  orderedEValues.forEach((eVal) => {
    const group1 = sheet1Groups[eVal];
    const group2 = sheet2Groups[eVal] || [];
    if (!group1 || group2.length === 0) return;

    counts[eVal] = (counts[eVal] || 0) + group1.length * group2.length;
    total += group1.length * group2.length;

    let lastSheet1Col2 = '';
    let lastSheet2Col2 = '';
    const schoolName = sheet1Data?.[0]?.[3] || '';
let lastFolderKey = null;

    group1.forEach((sheet1Row, rowIndex) => {
      gridCount = 0;
      if (!(doc.internal.getNumberOfPages() === 1 && rowIndex === 0)) doc.addPage();

      group2.forEach((sheet2Row, subjectIndex) => {
		  
		if (gridCount > 0 && gridCount % maxGridsPerPage === 0) doc.addPage();

        const col = gridCount % finalCols;
        const row = Math.floor((gridCount / finalCols) % finalRows);
        const x = marginX + col * (gridWidth + gap);
        const y = marginY + row * (gridHeight + gap);

       // Use Sheet1 column[2] as image match key
const imageKey = (sheet1Row[2] || '').toString().trim().toLowerCase();


// start with no photo
let photoImg = null;

// --- MODE: all (existing behavior) ---
if (mode === 'all') {
  // existing strict selection + fallback logic (keeps current behavior)
  photoImg = passportImagesMap[imageKey]; // exact student image if present

  if (!photoImg) {
    // pick folder key strictly from sheet1 col[5] (prefer) else sheet2 col[5]
    let rawFolderValue = sheet1Row?.[4] ?? sheet2Row?.[4];
    let folderKey = normalizeFolderKey(rawFolderValue);

    // carry-forward logic
    if (!folderKey && lastFolderKey) folderKey = lastFolderKey;
    if (folderKey) lastFolderKey = folderKey;

    if (folderKey) {
      const list = folderFallbacks[folderKey];
      if (Array.isArray(list) && list.length > 0) {
        folderImageTracker[folderKey] = folderImageTracker[folderKey] || 0;
        const idx = folderImageTracker[folderKey] % list.length;
        photoImg = list[idx];
        folderImageTracker[folderKey] += 1;
      } else {
        // requested folder exists but empty -> strict rule: no fallback to other folders
        photoImg = null;
      }
    } else {
      // no folder specified: use general pool
      if (generalPool && generalPool.length) {
        const idx = (typeof generalCounter !== 'undefined' ? generalCounter : 0) % generalPool.length;
        photoImg = generalPool[idx];
        generalCounter = (typeof generalCounter !== 'undefined' ? generalCounter + 1 : 1);
      } else {
        photoImg = null;
      }
    }
  }
}
// --- MODE: semi (rotate per-student) ---
else if (mode === 'semi') {
  // Unique key for each student or image
  const key = imageKey || '__unknown__';

  // Initialize rotation tracker for this student
  if (!studentRotationTracker[key]) {
    studentRotationTracker[key] = { counter: 0, sequence: [] };
  }

  const tracker = studentRotationTracker[key];

  // Build sequence only once per student
  if (!tracker.sequence.length) {
    const matched = passportImagesMap[imageKey] ? [passportImagesMap[imageKey]] : [];
    const rawFolderValue = sheet1Row?.[4] ?? sheet2Row?.[4];
    const folderKey = normalizeFolderKey(rawFolderValue);

    // Determine fallback pool
    const folderImgs =
      (folderKey && Array.isArray(folderFallbacks[folderKey]) && folderFallbacks[folderKey].length)
        ? folderFallbacks[folderKey]
        : (generalPool && generalPool.length ? generalPool : []);

    // ✅ Build alternating sequence starting with matched image
    const seq = [];
    if (matched.length) seq.push(matched[0]); // start with matched image first

    if (folderImgs.length && matched.length) {
      folderImgs.forEach(img => {
        seq.push(img);
        seq.push(matched[0]); // matched after every fallback
      });
    } else if (folderImgs.length) {
      seq.push(...folderImgs);
    } else if (matched.length) {
      seq.push(...matched);
    }

    tracker.sequence = seq;
  }

  // Pick next image from rotation
  if (tracker.sequence.length > 0) {
    const idx = tracker.counter % tracker.sequence.length;
    photoImg = tracker.sequence[idx];
    tracker.counter += 1;
  } else {
    photoImg = null;
  }
}


// else: unknown mode -> default to existing all-mode behavior
else {
  photoImg = passportImagesMap[imageKey] || null;
}
// ---------- end strict selection ----------





        // ✅ Border image
        const borderImg = borderImages[subjectIndex % borderImages.length];
        if (borderImg) {
          const borderFormat = borderImg.startsWith('data:image/png') ? 'PNG'
            : borderImg.startsWith('data:image/webp') ? 'WEBP' : 'JPEG';
          doc.addImage(borderImg, borderFormat, x, y, gridWidth, gridHeight);
        }
		// Kiss Cut
doc.setDrawColor(255, 0, 255);
doc.setLineWidth(0.05);

const cutInset = 0.15;

doc.rect(
  x + cutInset,
  y + cutInset,
  gridWidth - (cutInset * 2),
  gridHeight - (cutInset * 2)
);

        // ✅ Photo
const photoX = x + 5.2;
const photoY = y + 20;
const photoWidth = 19.8;
const photoHeight = 25.5;
const radius = 2.5;

if (photoImg) {
  try {
    const imgData = photoImg && photoImg.default ? photoImg.default : photoImg;
    const fmt = (typeof imgData === 'string' && imgData.startsWith('data:image/png')) ? 'PNG' : 'JPEG';
    doc.addImage(imgData, fmt, photoX, photoY, photoWidth, photoHeight);
    doc.setDrawColor(128, 0, 128);
    doc.setLineWidth(0.35);
    doc.roundedRect(photoX, photoY, photoWidth, photoHeight, radius, radius, 'S');
  } catch (err) {
    // fallback: draw placeholder box if addImage fails
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.roundedRect(photoX, photoY, photoWidth, photoHeight, radius, radius, 'S');
    console.warn('photo addImage failed', err);
  }
}



        // If no match, just skip adding any image for this grid

 {/*
            let textY = y + 22.5;
            const textX = x + 26; //to adjust all labels and values. 28 is adjusted to 26
            const gridFont = fontStyles[subjectIndex % fontStyles.length];
            const name = sheet1Row[0] || '';
            const admNo = sheet1Row[1] || '';
            const subject = sheet2Row[0] || '';
            
            const ordinal = getOrdinalSuffix(eVal);
             let studyText;
            if (ordinal) {
              studyText = `${ordinal.number}${ordinal.suffix} Class`;
            } else {
              studyText = `${eVal}`;
            }

            writeRow(`${labels.name}:`, name, 0, gridFont, textX, textY, true);

            textY += 6;

            const font = gridFont;
            const labelColor = labelColors[1 % labelColors.length];
            const valueColor = valueColors[1 % valueColors.length];
            const lineColor = lineColors[1 % lineColors.length];

            doc.setFont(font.font, font.fontWeight || 'normal');
            doc.setFontSize(font.size);
            doc.setTextColor(...hexToRgb(labelColor));
			const rowOffset = 1; // shift all except first row
			
            doc.text(`${labels.study}:`, textX, textY + rowOffset);
	
           // let valueX = textX + 18 - 5; // adjust class value to label


let valueX = textX + 18 - 5; // adjust class value to label

if (ordinal) {
  // Use the normal ordinal logic (e.g., "10th" derived from numeric eVal)
  const numberPart = ordinal.number;
  const suffixPart = ordinal.suffix;
  const remainingPart = ` ${classLabel}`;

  // Draw number part
  doc.setTextColor(...hexToRgb(valueColor));
  doc.setFontSize(font.size);
  doc.text(`${numberPart}`, valueX, textY + rowOffset);

  const numWidth = doc.getTextWidth(`${numberPart}`);

  // Draw superscript for suffix
  doc.setFontSize(font.size * 0.75);
  doc.text(`${suffixPart}`, valueX + numWidth, textY - 1.8 + rowOffset);

  const suffixWidth = doc.getTextWidth(`${suffixPart}`);

  // Draw remaining text (Class / Standard / Grade)
  doc.setFontSize(font.size);
  doc.text(remainingPart, valueX + numWidth + suffixWidth, textY + rowOffset);

  // ✅ Add sheet1Row[2] 2px after
  const totalWidth = numWidth + suffixWidth + doc.getTextWidth(remainingPart);
  const col2Value = sheet1Row[2]?.trim() || lastSheet1Col2;
  if (sheet1Row[2]?.trim()) lastSheet1Col2 = sheet1Row[2].trim();
  doc.text(col2Value, valueX + totalWidth + 2, textY + rowOffset);
} 

else if (/\d+(st|nd|rd|th)/i.test(eVal)) {
  // ✅ Detect number + suffix (like "1st", "2nd", "3rd", "10th")
  const match = eVal.match(/(\d+)(st|nd|rd|th)/i);
  const numberPart = match?.[1] || eVal;
  const suffixPart = match?.[2] || '';

  // ✅ Split prefix and suffix
  const beforeText = eVal.split(match[0])[0]?.trim() || '';  // e.g., "Intermediate"
  const afterText = eVal.split(match[0])[1]?.trim() || '';   // e.g., "Year"

  // ✅ Only include classLabel if:
  // user selected one AND there is NO text after the superscript (no "Year", "Std", etc.)
  const shouldAddLabel = classLabel && !afterText;
  const labelText = shouldAddLabel ? ` ${classLabel}` : '';

  doc.setTextColor(...hexToRgb(valueColor));
  doc.setFontSize(font.size);

  let currentX = valueX;

  // --- Draw before text ("Intermediate")
  if (beforeText) {
    doc.text(beforeText, currentX, textY + rowOffset);
    currentX += doc.getTextWidth(beforeText + ' ');
  }

  // --- Draw number ("1")
  doc.text(numberPart, currentX, textY + rowOffset);
  const numWidth = doc.getTextWidth(numberPart);
  currentX += numWidth;

  // --- Draw superscript suffix ("st")
  doc.setFontSize(font.size * 0.75);
  doc.text(suffixPart, currentX, textY - 1.8 + rowOffset);
  const suffixWidth = doc.getTextWidth(suffixPart);
  currentX += suffixWidth;

  // --- Draw optional label (Class / Grade / Standard)
  if (shouldAddLabel) {
    doc.setFontSize(font.size);
    doc.text(labelText, currentX, textY + rowOffset);
    currentX += doc.getTextWidth(labelText);
  }

  // --- Draw after text ("Year")
  if (afterText) {
    doc.setFontSize(font.size);
    doc.text(' ' + afterText, currentX, textY + rowOffset);
    currentX += doc.getTextWidth(' ' + afterText);
  }

  // --- Finally add Sheet1 Column 2 value (2px after)
  const col2Value = sheet1Row[2]?.trim() || lastSheet1Col2;
  if (sheet1Row[2]?.trim()) lastSheet1Col2 = sheet1Row[2].trim();
  doc.setFontSize(font.size);
  doc.text(col2Value, currentX + 2, textY + rowOffset);
}

else {
  // Fallback: normal text if no numeric suffix pattern
  doc.setTextColor(...hexToRgb(valueColor));
  doc.setFontSize(gridFont.size);
  doc.text(`${eVal}`, valueX, textY + rowOffset);

  const eValWidth = doc.getTextWidth(`${eVal}`);
  const col2Value = sheet1Row[2]?.trim() || lastSheet1Col2;
  if (sheet1Row[2]?.trim()) lastSheet1Col2 = sheet1Row[2].trim();
  doc.text(col2Value, valueX + eValWidth + 2, textY + rowOffset);
}


// underline
doc.setDrawColor(...hexToRgb(lineColor));
doc.setLineWidth(0.25);
doc.line(textX, textY + 1.5 + rowOffset, textX + gridWidth - 30.4, textY + 1.5 + rowOffset);
            textY += 6;
			
		
	
			
if (admNo) {
  writeRow(`${labels.admNo}:`, admNo, 2, gridFont, textX, textY + rowOffset, false, rowOffset);

  // ✅ Write sheet2Row[2] 2px after AdmNo
  const col2Value2 = sheet2Row[2]?.trim() || lastSheet2Col2; // carry forward last non-empty
  if (sheet2Row[2]?.trim()) lastSheet2Col2 = sheet2Row[2].trim();

  const admNoWidth = doc.getTextWidth(admNo);
  doc.setFont(gridFont.font, gridFont.fontWeight || 'normal');
  doc.setFontSize(gridFont.size);
  doc.setTextColor(...hexToRgb(valueColors[2 % valueColors.length]));
  doc.text(col2Value2, textX + 18 + admNoWidth - 3, textY + rowOffset+1); // adjust 2px after AdmNo

  textY += 6;
}

if (subject) {
  writeRow('Subject:', subject, 3, gridFont, textX, textY + rowOffset+1, false, rowOffset);
  textY += 6;
}


if (schoolName) {
  writeRow(`${labels.school}:`, schoolName, 4, gridFont, textX, textY + rowOffset+2, false, rowOffset);
}
*/}





const yShift = 1.25;   // <<< universal vertical spacing control

let textY = y + 22.5;
const textX = x + 26;
const gridFont = fontStyles[subjectIndex % fontStyles.length];


const schoolName = sheet1Data?.[0]?.[3] || '';

if (schoolName) {
  doc.setFont(gridFont.font, 'bold');
  doc.setFontSize(13);

  // Gold color
  doc.setTextColor(138, 43, 226);

  doc.text(
    schoolName,
    x + (gridWidth / 2),
    textY - 6,
    { align: 'center' }
  );
}
const name = sheet1Row[0] || '';
const admNo = sheet1Row[1] || '';
const subject = sheet2Row[0] || '';

const ordinal = getOrdinalSuffix(eVal);
let studyText;
if (ordinal) {
  studyText = `${ordinal.number}${ordinal.suffix} Class`;
} else {
  studyText = `${eVal}`;
}

writeRow(`${labels.name}:`, name, 0, gridFont, textX, textY, true);

textY += 6;

const font = gridFont;
const labelColor = labelColors[1 % labelColors.length];
const valueColor = valueColors[1 % valueColors.length];
const lineColor = lineColors[1 % lineColors.length];

doc.setFont(font.font, font.fontWeight || 'normal');
doc.setFontSize(font.size);
doc.setTextColor(...hexToRgb(labelColor));
const rowOffset = 1;

doc.text(`${labels.study}:`, textX, textY + rowOffset + yShift);

let valueX = textX + 18 - 5;

if (ordinal) {
  const numberPart = ordinal.number;
  const suffixPart = ordinal.suffix;
  const remainingPart = ` ${classLabel}`;

  doc.setTextColor(...hexToRgb(valueColor));
  doc.setFontSize(font.size);
  doc.text(`${numberPart}`, valueX, textY + rowOffset + yShift);

  const numWidth = doc.getTextWidth(`${numberPart}`);

  doc.setFontSize(font.size * 0.75);
  doc.text(`${suffixPart}`, valueX + numWidth, textY - 1.8 + rowOffset + yShift);

  const suffixWidth = doc.getTextWidth(`${suffixPart}`);

  doc.setFontSize(font.size);
  doc.text(remainingPart, valueX + numWidth + suffixWidth, textY + rowOffset + yShift);

  const totalWidth = numWidth + suffixWidth + doc.getTextWidth(remainingPart);
  const col2Value = sheet1Row[2]?.trim() || lastSheet1Col2;
  if (sheet1Row[2]?.trim()) lastSheet1Col2 = sheet1Row[2].trim();
  doc.text(col2Value, valueX + totalWidth + 2, textY + rowOffset + yShift);

} else if (/\d+(st|nd|rd|th)/i.test(eVal)) {

  const match = eVal.match(/(\d+)(st|nd|rd|th)/i);
  const numberPart = match?.[1] || eVal;
  const suffixPart = match?.[2] || '';
  const beforeText = eVal.split(match[0])[0]?.trim() || '';
  const afterText = eVal.split(match[0])[1]?.trim() || '';
  const shouldAddLabel = classLabel && !afterText;
  const labelText = shouldAddLabel ? ` ${classLabel}` : '';

  doc.setTextColor(...hexToRgb(valueColor));
  doc.setFontSize(font.size);

  let currentX = valueX;

  if (beforeText) {
    doc.text(beforeText, currentX, textY + rowOffset + yShift);
    currentX += doc.getTextWidth(beforeText + ' ');
  }

  doc.text(numberPart, currentX, textY + rowOffset + yShift);
  const numWidth = doc.getTextWidth(numberPart);
  currentX += numWidth;

  doc.setFontSize(font.size * 0.75);
  doc.text(suffixPart, currentX, textY - 1.8 + rowOffset + yShift);
  const suffixWidth = doc.getTextWidth(suffixPart);
  currentX += suffixWidth;

  if (shouldAddLabel) {
    doc.setFontSize(font.size);
    doc.text(labelText, currentX, textY + rowOffset + yShift);
    currentX += doc.getTextWidth(labelText);
  }

  if (afterText) {
    doc.setFontSize(font.size);
    doc.text(' ' + afterText, currentX, textY + rowOffset + yShift);
    currentX += doc.getTextWidth(' ' + afterText);
  }

  const col2Value = sheet1Row[2]?.trim() || lastSheet1Col2;
  if (sheet1Row[2]?.trim()) lastSheet1Col2 = sheet1Row[2].trim();
  doc.setFontSize(font.size);
  doc.text(col2Value, currentX + 2, textY + rowOffset + yShift);

} else {

  doc.setTextColor(...hexToRgb(valueColor));
  doc.setFontSize(gridFont.size);
  doc.text(`${eVal}`, valueX, textY + rowOffset + yShift);

  const eValWidth = doc.getTextWidth(`${eVal}`);
  const col2Value = sheet1Row[2]?.trim() || lastSheet1Col2;
  if (sheet1Row[2]?.trim()) lastSheet1Col2 = sheet1Row[2].trim();
  doc.text(col2Value, valueX + eValWidth + 2, textY + rowOffset + yShift);
}

doc.setDrawColor(...hexToRgb(lineColor));
doc.setLineWidth(0.25);
doc.line(textX, textY + 1.5 + rowOffset + yShift, textX + gridWidth - 30.4, textY + 1.5 + rowOffset + yShift);

textY += 6;

if (admNo) {
  writeRow(`${labels.admNo}:`, admNo, 2, gridFont, textX, textY + 1.5 + rowOffset + yShift, false, rowOffset);

  const col2Value2 = sheet2Row[2]?.trim() || lastSheet2Col2;
  if (sheet2Row[2]?.trim()) lastSheet2Col2 = sheet2Row[2].trim();

  const admNoWidth = doc.getTextWidth(admNo);
  doc.setFont(gridFont.font, gridFont.fontWeight || 'normal');
  doc.setFontSize(gridFont.size);
  doc.setTextColor(...hexToRgb(valueColors[2 % valueColors.length]));
  doc.text(col2Value2, textX + 18 + admNoWidth - 2, textY + 2.5 + rowOffset + yShift);

  textY += 6;
}

if (subject) {
  writeRow('Subject:', subject, 3, gridFont, textX, textY + rowOffset + 3.5 + yShift, false, rowOffset);
  textY += 6;
}







            
        gridCount++;
      });
	  
	  // AFTER group2.forEach(...) finishes for this student/group:
// fill remaining empty grids ONCE for the current page
fillRemainingGrids(gridCount, doc, maxGridsPerPage, finalCols, finalRows, gridWidth, gridHeight, gap, marginX, marginY);


	  
    });
  });

// ✅ Final page fill (in case the last page is not full)
fillRemainingGrids(gridCount, doc, maxGridsPerPage, finalCols, finalRows, gridWidth, gridHeight, gap, marginX, marginY);



  // ✅ Save PDF
  const schoolName = sheet1Data?.[0]?.[3] || 'output';
  doc.save(`${schoolName}.pdf`);

  setGridCounts(counts);
  setTotalGrids(total);
  

}
